import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "@mountayaapp/locales";

import { type DocEntry, findDoc } from "./registry";

/**
 * ResolvedDoc is the outcome of matching a pathname to authored content: the
 * resolved language and slug, plus the matching page or `null` when no page is
 * registered at that location (a 404).
 */
export interface ResolvedDoc {
  doc: DocEntry | null;
  lang: SupportedLanguage;
  slug: string;
}

/**
 * isSupportedLanguage narrows a path segment to a supported language, so only an
 * actual language prefix is treated as one.
 */
const isSupportedLanguage = (value: string): value is SupportedLanguage =>
  (SUPPORTED_LANGUAGES as readonly string[]).includes(value);

/**
 * resolveDoc maps a URL pathname to its authored page. Every docs URL is
 * always-prefixed with its language, so a pathname resolves to a page only when
 * its first segment is a supported language: `/en/authentication/` resolves the
 * English authentication page and `/en/` the English root. A pathname whose first
 * segment is not a supported language is not a valid docs route — an unknown
 * prefix (`/foobar/authentication/`) or a missing prefix (`/authentication/`) —
 * so it resolves to a `null` doc for the 404 path rather than silently serving
 * the default language's matching page; the default language still rides along
 * for the not-found view's recovery link, and the slug carries the whole path for
 * reference. Leading and trailing slashes are normalized away first, so the
 * trailing-slash directory URLs the site links to resolve the same as their
 * slash-free form.
 */
export const resolveDoc = (pathname: string): ResolvedDoc => {
  const segments = pathname.split("/").filter(Boolean);
  const [first] = segments;

  if (first !== undefined && isSupportedLanguage(first)) {
    const slug = segments.slice(1).join("/");

    return { doc: findDoc(first, slug), lang: first, slug };
  }

  return { doc: null, lang: DEFAULT_LANGUAGE, slug: segments.join("/") };
};
